import React, { FC, useState } from "react";
import { GatsbyImage } from "gatsby-plugin-image";
import { getGatsbyImage } from "gatsby-plugin-storyblok-image";
import classNames from "classnames";
import loadable from "@loadable/component";
import {
  NODE_PARAGRAPH,
  NODE_HEADING,
  render,
} from "storyblok-rich-text-react-renderer";
import {
  Asset,
  Button,
  MediaOptions,
  RichText,
} from "@app/components/base-page/models";
import { ContentAlign } from "@app/components/base-page/enums";
import { FileType, checkFileType } from "@app/helpers/check-file-type";
import { useGenericData } from "@app/hooks/use-generic-data/useGenericData";
import { useIsXLMediaQuery, useScroll, useTransform } from "@app/hooks";
import { LinkButtonAdapter } from "../../../link-button-adapter/LinkButtonAdapter";

const DEFAULT_OVERLAY = 30;
const ReactPlayer = loadable(() => import("react-player"));

type Props = {
  title: RichText;
  subtitle: RichText;
  contentAlign: ContentAlign;
  media: Asset;
  mediaOptions: MediaOptions[];
  button?: Button[];
};

export const HeroAnimated: FC<Props> = ({
  title,
  button,
  media,
  mediaOptions,
}) => {
  const isXL = useIsXLMediaQuery();
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  const translateY = useTransform(scrollY, [0, 1000], [0, 120]);
  const [isBackgroundReady, setIsBackgroundReady] = useState(
    checkFileType(media.filename) !== FileType.VIDEO,
  );
  const { notificationBar } = useGenericData();

  return (
    <div
      className={classNames(
        "bg-primary relative flex min-h-screen items-end justify-center px-5 pb-32",
        "md:items-center md:justify-start md:px-15 md:pb-0",
        "xl:overflow-hidden xl:px-24 xl:pt-10",
        {
          "min-h-[calc(100vh-38px)]": notificationBar?.isEnabled,
          "min-h-screen": !notificationBar?.isEnabled,
        },
      )}
    >
      <div
        className={classNames(
          "h-full",
          "xl:relative xl:mx-auto xl:w-full xl:max-w-[1920px]",
        )}
      >
        <div
          className={classNames(
            "absolute top-0 left-0 h-full w-full",
            "xl:flex xl:items-center xl:justify-end",
          )}
        >
          <div
            className={classNames(
              "h-full w-full",
              "xl:h-fit xl:w-[75%]",
              isBackgroundReady
                ? "xl:animate-slide xl:[animation-delay:0.1s] xl:[animation-duration:1.4s]"
                : "xl:hidden",
            )}
            style={
              isXL
                ? { transform: `translate(0px, ${translateY}px)` }
                : { transform: "translate(0px, 0px)" }
            }
          >
            {checkFileType(media.filename) === FileType.VIDEO ? (
              <ReactPlayer
                className={classNames(
                  "aspect-square h-full [&>video]:rounded-none [&>video]:object-cover",
                  "xl:aspect-auto xl:h-[calc(100vh-280px)] [&>video]:xl:rounded-lg",
                )}
                url={media.filename}
                width="100%"
                height="none"
                playing
                muted
                loop
                playsinline
                onReady={() => setIsBackgroundReady(true)}
                config={{
                  file: {
                    attributes: { disablePictureInPicture: true },
                  },
                }}
              />
            ) : (
              <GatsbyImage
                image={getGatsbyImage(media.filename, { layout: "fullWidth" })}
                alt={media.alt}
                className={classNames(
                  "aspect-square h-full w-full rounded-none",
                  "xl:h-[calc(100vh-280px)] xl:rounded-lg",
                )}
              />
            )}
          </div>
        </div>

        <div
          className={classNames("absolute inset-0", "xl:hidden")}
          style={{
            backgroundColor: `rgba(0, 0, 0, ${Number(mediaOptions[0]?.overlay ?? DEFAULT_OVERLAY) / 100})`,
          }}
        ></div>

        <div
          className={classNames("relative overflow-hidden")}
          style={isXL ? { opacity } : { opacity: 1 }}
        >
          <h1
            className={classNames(
              "text-large text-primary-content",
              "lg:text-xx-large",
              "xl:text-huge",
              isBackgroundReady
                ? "xl:[&>span:nth-child(1)]:[animation-delay:0.2s] xl:[&>span:nth-child(1)]:[animation-duration:1.3s] xl:[&>span:nth-child(2)]:[animation-delay:0.3s] xl:[&>span:nth-child(2)]:[animation-duration:1.2s]"
                : "xl:hidden",
            )}
          >
            {render(title, {
              nodeResolvers: {
                [NODE_PARAGRAPH]: (children) =>
                  React.createElement(
                    "span",
                    {
                      className: classNames(
                        "block",
                        isBackgroundReady ? "xl:animate-slide" : "xl:hidden",
                      ),
                    },
                    children,
                  ),
                [NODE_HEADING]: (children) =>
                  React.createElement(
                    "span",
                    {
                      className: classNames(
                        "block",
                        isBackgroundReady ? "xl:animate-slide" : "xl:hidden",
                      ),
                    },
                    children,
                  ),
              },
            })}
          </h1>
          {button?.length ? (
            <div
              className={classNames(
                "mt-8",
                isBackgroundReady
                  ? "xl:animate-slide xl:[animation-delay:0.4s] xl:[animation-duration:1.1s]"
                  : "xl:hidden",
              )}
            >
              <LinkButtonAdapter data={button[0]} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
